import { useEffect, useRef, useState } from 'react';

import { Formik, Form as FormikForm } from 'formik';
import * as Yup from 'yup';

import { useBankAccountLink } from 'v3/hooks/useBankAccountLink';
import { PaymentParams } from 'v3/api/client/paymentParams';
import { isPlaidBankLink } from 'v3/api/core/apiConfig';
import { useExperiment } from '@marvelapp/react-ab-test';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';

import { Check } from '@styled-icons/remix-fill/Check';
import IconItem from 'v3/components/IconItem';
import { Bank, Close, Lock } from '@styled-icons/remix-line';
import Checkbox from 'v3/components/Checkbox';
import Button from 'v3/components/Button';
import ModalNotification from 'v3/components/ModalNotification';
import PromotionalModal from 'v3/components/PromotionalModal';
import ErrorFocus from 'v3/components/ErrorFocus';

import WhitePlaidIco from 'v3/assets/svg/white-plaid-icon.svg';


import theme from 'styles/theme';
import * as S from '../styles';


const validationSchema = Yup.object().shape({
  notice1: Yup.boolean()
    .required('This field is required')
    .oneOf([true], 'This field is required'),
});

type ACHFormProps = {
  cfUuid: string;
  submitPaymentData: (paymentParams: PaymentParams) => void;
  validateOnSubmit?: () => boolean;
};

export const ACHForm = ({ 
  cfUuid,
  submitPaymentData,
  validateOnSubmit = () => true,
}: ACHFormProps) => {
  const { leadData } = useSelector((state: RootState) => state.lead);
  const plaidTriggerFunc = useRef<Function>(() => {}); 
  const [openExitModal, setOpenExitModal] = useState(false);
  const { selectVariant } = useExperiment('Plaid On Exit');
  const showPromotionalModal = selectVariant({
    A: true,
    B: false,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isProcessing, setIsProcessing] = useState('');

  const initialValues = {
    notice1: false,
  };

  return (
    <Formik
      isInitialValid={false}
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={async () => {

        if(!validateOnSubmit()) {
          return;
        }
       
        setIsLoading(true);
        setIsProcessing('checking_bank_account');
        plaidTriggerFunc.current();
        
      }}
    >
      {(formik) => {
        const { errors, touched, isValid } = formik;

        const onExit = () => {
          setOpenExitModal(true);
          setIsLoading(false);
          setIsProcessing('');
        };

        const { open, linkError } = useBankAccountLink({
          signContract: submitPaymentData,
          cfUuid,
          setIsLoading,
          setIsProcessing,
          onExit,
          billingDetails: {
            name: `${leadData.firstName ?? ''} ${leadData.lastName ?? ''}`.trim(),
            email: leadData.email,
          },
        });
        
        plaidTriggerFunc.current = open;
        
        const loading = isLoading;

        const buttonStatus = () => {
          if (loading || isProcessing === 'checking_bank_account') {
            return (
              <>
                <span>Selecting bank account</span> <S.ProcessingIcon size={18} />
              </>
            );
          }

          if (loading || isProcessing === 'processing') {
            return (
              <>
                <span>Processing payment</span> <S.ProcessingIcon size={18} />
              </>
            );
          }

          if (isProcessing === 'complete') {
            return (
              <>
                <span>All Done!</span> <Check size={18} />
              </>
            );
          }

          if (linkError) {
            return 'Try again';
          }

          return 'Choose bank account';
        };

        return (
          <>
            {showPromotionalModal ? (
              <PromotionalModal
                title={
                  <>
                    Don't leave yet, <br /> <span>Get $50</span> off
                  </>
                }
                content={
                  <S.PromotionalModalContent>
                    <p>
                      <strong>Sign up and pay now</strong> and get <br />
                      $50 off on your loan amount
                    </p>

                    <div>
                      Still have questions?{' '}
                      <a id="contact-us-now">talk to us now</a>
                    </div>
                  </S.PromotionalModalContent>
                }
                onCancel={() => setOpenExitModal(false)}
                open={openExitModal}
              />
            ) : (
              <ModalNotification
                title="Whoops..."
                content="Looks like we’re still missing some bank account information. No worries, hit the button to retry entering your info!"
                buttonText="try again"
                onCancel={() => setOpenExitModal(false)}
                onClick={() => open()}
                open={openExitModal}
              />
            )}

            <FormikForm>
              <S.EncryptContent>
                <IconItem
                  icon={<Lock size={12} />}
                  text={
                    <S.LockText>
                      This is a secure and verified way to approve your account.
                    </S.LockText>
                  }
                />
              </S.EncryptContent>

              <S.Checkboxes>
                <S.FieldWrapper>
                  <Checkbox
                    name="notice1"
                    required
                    feedbackMessage={touched.notice1 && errors.notice1}
                    isValid={!(touched.notice1 && errors.notice1)}
                  >
                    <>
                      I have read, understand and agree to{' '}
                      <strong>ACH Authorization</strong> and{' '}
                      <strong>Savings Club</strong>{' '}
                      <Button
                        size="small"
                        variant="anchor"
                        as="a"
                        href="#agreement"
                      >
                        Participation Agreement
                      </Button>
                      .
                    </>
                  </Checkbox>
                </S.FieldWrapper>
              </S.Checkboxes>

              <S.ButtonSection>
                <S.Buttons>
                  {linkError && (
                    <S.ErrorMessage>
                      <Close size={20} color={theme.colors.statusDanger30} />
                      <p>Error processing your account</p>
                    </S.ErrorMessage>
                  )}

                  <Button
                    icon={
                      loading ||
                      isProcessing === 'processing' ||
                      isProcessing === 'checking_bank_account' ||
                      isProcessing === 'complete' ? undefined : isPlaidBankLink ? (
                        <img src={WhitePlaidIco} alt="Plaid logo" />
                      ) : (
                        <Bank size={18} />
                      )
                    }
                    status={
                      isProcessing === 'processing'
                        ? 'info'
                        : isProcessing === 'complete'
                        ? 'success'
                        : undefined
                    }
                    iconPosition="left"
                    variant="primary"
                    disabled={
                      !isValid ||
                      loading ||
                      isProcessing === 'processing' ||
                      isProcessing === 'complete'
                    }
                    type="submit"
                  >
                    {buttonStatus()}
                  </Button>
                </S.Buttons>
              </S.ButtonSection>

              <ErrorFocus />
            </FormikForm>
          </>
        );
      }}
    </Formik>
  );
};
